import type {
  Leaderboard as LbLeaderboard,
  LeaderboardTable as LbTable,
  LeaderboardRun as LbRun,
  LeaderboardAlgorithm as LbAlgorithm,
  Submission as LbSubmission,
  MetaDataset as LbMetaDataset,
  MetaDatasetPayload as LbMetaDatasetPayload,
  LeaderboardProvider as LbProvider,
  CreateRunPayload as LbCreateRunPayload,
  Paged as LbPaged,
} from '../../types/leaderboard';
import { ApiResponse } from '../../types/api';
import { api } from './client';

// ─── Лидерборд алгоритмов Prompt Tuning (эпик AIT-109) ──────────────────────
//
// Всё под /auto-tune/leaderboards в обычной оболочке {success, data}.
// Списки приходят как {items, pagination}.
export const leaderboard = {
  /** Список лидербордов — по одному на мета-датасет. */
  list: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<LbPaged<LbLeaderboard>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbLeaderboard>>>('/auto-tune/leaderboards/', { params });
    return response.data;
  },

  /**
   * Таблица лидерборда. Строки уже отсортированы бэкендом по первичной метрике
   * с учётом направления — своя сортировка перевернёт её при `minimize`.
   * Пустая таблица (`rows: []`) — нормальный ответ, а не 404.
   */
  getTable: async (leaderboardId: string, partition?: string | null): Promise<ApiResponse<LbTable>> => {
    // Внешний актор: рейтинг внутри одной partition. Без параметра бэкенд берёт
    // partition последнего подготовленного прогона; локальные борды его игнорируют.
    const response = await api.get<ApiResponse<LbTable>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/table`,
      partition ? { params: { benchmark_partition_sha256: partition } } : undefined
    );
    return response.data;
  },

  /**
   * История сабмишенов алгоритма, свежие первыми.
   * `algorithmId` — внутренний UUID из `row.algorithm.id`, не строка `legacy:...@...`.
   */
  listSubmissions: async (
    leaderboardId: string,
    algorithmId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<LbPaged<LbSubmission>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbSubmission>>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/algorithms/${encodeURIComponent(algorithmId)}/submissions`,
      { params }
    );
    return response.data;
  },

  // ── Мета-датасеты ─────────────────────────────────────────────────────────

  /** Лидерборд создаётся вместе с мета-датасетом, отдельного вызова не нужно. */
  createMetaDataset: async (payload: LbMetaDatasetPayload): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.post<ApiResponse<LbMetaDataset>>('/auto-tune/leaderboards/meta-datasets', payload);
    return response.data;
  },

  listMetaDatasets: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<LbPaged<LbMetaDataset>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbMetaDataset>>>('/auto-tune/leaderboards/meta-datasets', {
      params,
    });
    return response.data;
  },

  getMetaDataset: async (id: string): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.get<ApiResponse<LbMetaDataset>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`
    );
    return response.data;
  },

  /** Состав присылается целиком, дельтой нельзя. */
  updateMetaDataset: async (id: string, payload: LbMetaDatasetPayload): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.put<ApiResponse<LbMetaDataset>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`,
      payload
    );
    return response.data;
  },

  /** Удаляет вместе с лидербордом и всей историей сабмишенов. */
  deleteMetaDataset: async (id: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`
    );
    return response.data;
  },

  // ── Алгоритмы и прогоны ───────────────────────────────────────────────────

  /** Отключённые показываем отдельно и с `reason` — отправить их бэкенд не даст. */
  listAlgorithms: async (params?: {
    include_disabled?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<LbPaged<LbAlgorithm>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbAlgorithm>>>('/auto-tune/leaderboards/algorithms', { params });
    return response.data;
  },

  syncAlgorithms: async (): Promise<ApiResponse<unknown>> => {
    const response = await api.post<ApiResponse<unknown>>('/auto-tune/leaderboards/algorithms/sync');
    return response.data;
  },

  /**
   * Провайдеры и их модели для формы мета-датасета (FE-1). Не кэшируется на
   * бэкенде: список локального шлюза меняется, когда на него заливают модель.
   */
  listProviders: async (): Promise<ApiResponse<{ providers: LbProvider[] }>> => {
    const response = await api.get<ApiResponse<{ providers: LbProvider[] }>>('/auto-tune/leaderboards/providers');
    return response.data;
  },

  createRun: async (leaderboardId: string, payload: LbCreateRunPayload): Promise<ApiResponse<LbRun>> => {
    const response = await api.post<ApiResponse<LbRun>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/runs`,
      payload
    );
    return response.data;
  },

  getRun: async (runId: string): Promise<ApiResponse<LbRun>> => {
    const response = await api.get<ApiResponse<LbRun>>(`/auto-tune/leaderboards/runs/${encodeURIComponent(runId)}`);
    return response.data;
  },

  cancelRun: async (runId: string): Promise<ApiResponse<LbRun>> => {
    const response = await api.post<ApiResponse<LbRun>>(
      `/auto-tune/leaderboards/runs/${encodeURIComponent(runId)}/cancel`
    );
    return response.data;
  },

  cancelSubmission: async (submissionId: string): Promise<ApiResponse<LbSubmission>> => {
    const response = await api.post<ApiResponse<LbSubmission>>(
      `/auto-tune/leaderboards/submissions/${encodeURIComponent(submissionId)}/cancel`
    );
    return response.data;
  },
};
